import React from 'react';
import PropTypes from 'prop-types';
import './LoginStitch.css';
import BrandLogo from './BrandLogo';

/**
 * LoginShell — the split-screen login chrome (Phase 6): brand panel on the
 * left, sign-in panel on the right. Both login flavors (Google SSO and the
 * oversea email/password form) render their form into `children`.
 * Styled on the standard slate/primary tokens — the Material token names the
 * original LoginStitch used are gone.
 */
const LoginShell = ({ subtitle = 'Sign in to your account to continue.', children }) => (
  <div className="tw-bg-slate-50 tw-text-slate-900 tw-antialiased">
    <main className="tw-min-h-screen tw-flex tw-flex-col lg:tw-flex-row">
      {/* Left: brand panel */}
      <section className="tw-hidden lg:tw-flex lg:tw-w-1/2 xl:tw-w-3/5 structural-gradient tw-relative tw-overflow-hidden tw-items-center tw-justify-center tw-p-24">
        <div className="tw-absolute tw-inset-0 tw-opacity-20 tw-pointer-events-none">
          <svg className="tw-w-full tw-h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
            <path d="M0 0 L100 100 M100 0 L0 100" fill="none" stroke="white" strokeWidth="0.1"></path>
            <circle cx="50" cy="50" fill="none" r="45" stroke="white" strokeWidth="0.05"></circle>
          </svg>
        </div>
        <div className="tw-relative tw-z-10 tw-max-w-xl">
          <div className="tw-mb-12">
            <span className="tw-text-xs tw-uppercase tw-tracking-[0.2em] tw-text-blue-200 tw-font-semibold tw-opacity-80">
              Industrial Excellence
            </span>
            <h1 className="tw-text-white tw-font-black tw-text-5xl lg:tw-text-7xl tw-mt-4 tw-leading-[0.9] tw-tracking-tighter">
              ENGINEERED
              <br />
              FOR STRENGTH
            </h1>
          </div>
          <div className="tw-space-y-8">
            <p className="tw-text-blue-200 tw-text-lg tw-font-light tw-leading-relaxed tw-max-w-lg">
              We aim to become the benchmark in the steel building industry,
              through innovative, safe, and sustainable solutions
            </p>
            <div className="tw-flex tw-gap-12 tw-pt-8">
              <div>
                <div className="tw-text-white tw-text-3xl tw-font-bold tw-tracking-tighter">6000+</div>
                <div className="tw-text-xs tw-uppercase tw-tracking-widest tw-text-blue-200 tw-mt-1">
                  Projects Delivered
                </div>
              </div>
              <div>
                <div className="tw-text-white tw-text-3xl tw-font-bold tw-tracking-tighter">50+</div>
                <div className="tw-text-xs tw-uppercase tw-tracking-widest tw-text-blue-200 tw-mt-1">
                  Countries Reached
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="tw-absolute tw-bottom-12 tw-left-12 tw-opacity-30">
          <BrandLogo alt="PEBSTEEL" className="tw-block tw-w-auto tw-h-auto tw-max-w-[104px] tw-max-h-9 tw-object-contain tw-brightness-0 tw-invert" />
        </div>
      </section>

      {/* Right: sign-in panel */}
      <section className="tw-w-full lg:tw-w-1/2 xl:tw-w-2/5 tw-bg-white tw-flex tw-flex-col tw-justify-center tw-px-6 sm:tw-px-12 lg:tw-px-16 xl:tw-px-24 tw-py-12 tw-relative tw-flex-1">
        <div className="tw-w-full tw-max-w-sm tw-mx-auto">
          <header className="tw-mb-12 tw-text-center lg:tw-text-left">
            <div className="tw-mb-10 tw-flex tw-justify-center lg:tw-block">
              <BrandLogo alt="PEBSTEEL" className="tw-block tw-w-auto tw-h-auto tw-max-w-[136px] tw-max-h-12 lg:tw-max-w-[150px] lg:tw-max-h-14 tw-object-contain tw-mx-auto lg:tw-mx-0" />
            </div>
            <h2 className="tw-font-bold tw-text-3xl tw-text-primary tw-tracking-tight tw-mb-2">
              Portal Workspace
            </h2>
            <p className="tw-text-slate-500 tw-text-sm">{subtitle}</p>
          </header>

          {children}

          <footer className="tw-mt-12 tw-pt-8 tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-items-center tw-gap-3">
              <a
                className="tw-text-xs tw-uppercase tw-tracking-widest tw-text-slate-400 hover:tw-text-primary tw-transition-colors"
                href="mailto:portal@pebsteel.com.vn"
              >
                Support
              </a>
              <span className="tw-w-1 tw-h-1 tw-bg-slate-300 tw-rounded-full" />
              <a
                className="tw-text-xs tw-uppercase tw-tracking-widest tw-text-slate-400 hover:tw-text-primary tw-transition-colors"
                href="/privacy-policy"
              >
                Privacy Policy
              </a>
            </div>
          </footer>
        </div>
        <div className="tw-mt-auto tw-pt-12 lg:tw-absolute lg:tw-bottom-6 lg:tw-left-0 lg:tw-right-0 tw-text-center lg:tw-text-left lg:tw-px-16 xl:tw-px-24">
          <p className="tw-text-[10px] tw-text-slate-400 tw-uppercase tw-tracking-widest">
            © 2019 PEBSTEEL BUILDINGS CO., LTD. ALL RIGHTS RESERVED.
          </p>
        </div>
      </section>
    </main>
  </div>
);

LoginShell.propTypes = {
  subtitle: PropTypes.node,
  children: PropTypes.node
};

export default LoginShell;
